import React from 'react'
import { string, func } from 'prop-types'
import { css } from '@emotion/core'
import { Paragraph, colors, IconButton } from '@fs/zion-ui'
import { MenuClose } from '@fs/zion-icon'
import { appPath } from '@fs/zion-config'
import { useTranslation } from 'react-i18next'
import { VisuallyHidden } from '@reach/visually-hidden'

const cardCss = css`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: 16px;
  background-color: ${colors.gray02};
`

const iconCss = css`
  flex-shrink: 0;
  width: 46px;
  height: 46px;
  border-radius: 10px;
`

const textCss = css`
  flex: 1 1 auto;
  min-width: 0;
`

const codeCss = css`
  flex-shrink: 0;
  font-family: 'SF Mono', 'Roboto Mono', Menlo, Consolas, monospace;
  font-variant-numeric: tabular-nums;
  font-size: 28px;
  font-weight: 700;
  color: ${colors.gray100};
  white-space: nowrap;
`

// "0 4 2 1 7" so screen readers announce one digit at a time instead of a five-digit number.
const spaceOutDigits = (code) => code.split('').join(' ')

export default function DiscoveryCodeCard({ code, onHide }) {
  const { t } = useTranslation()

  return (
    <div css={cardCss} data-testid="discovery-code-card">
      <img css={iconCss} src={`${appPath}/logo192.png`} alt="" aria-hidden="true" />
      <div css={textCss}>
        <Paragraph size="sm" dense>
          {t('discoveryCode.card.label', 'Your Discovery Code')}
        </Paragraph>
        <Paragraph size="xs" dense secondary>
          {t('discoveryCode.card.center', 'FamilySearch Center')}
        </Paragraph>
      </div>
      {/* aria-label is not reliably announced on a generic span, so the digits are hidden from assistive
          tech and a visually hidden spaced-out label is read instead. */}
      <span css={codeCss} aria-hidden="true">
        {code}
      </span>
      <VisuallyHidden>
        {t('discoveryCode.card.codeAccessibleLabel', 'Discovery Code {digits}', { digits: spaceOutDigits(code) })}
      </VisuallyHidden>
      <IconButton
        title={t('discoveryCode.card.hide', 'Hide Discovery Code')}
        aria-label={t('discoveryCode.card.hide', 'Hide Discovery Code')}
        Icon={MenuClose}
        color="gray"
        onClick={onHide}
      />
    </div>
  )
}

DiscoveryCodeCard.propTypes = {
  code: string.isRequired,
  onHide: func.isRequired,
}
